import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { get, map, isEmpty, zipObject, has, uniqBy, filter } from 'lodash/fp';
import { Button, Row, Col, Loader, DateRangePicker, Checkbox } from 'rsuite';
import { useDispatch, useSelector } from 'react-redux';
import {
  getIsFetching,
  getMeta,
  getOrderedEvents
} from '../../../redux/Reservation/selectors/available-events';
import { fetchReservationAvailableEvents } from '../../../redux/Reservation/sagas/routines';
import { clearReservationAvailableEvents } from '../../../redux/Reservation/actions/available-events';
import OutreachColors from '../../../constants/reservation/OutreachColors';
import {
  setShowAvailableEventsOnlyCookie,
  getShowAvailableEventsOnlyCookie
} from '../../../services/currentUserCookie';
import TimeSlotsSelection from './TimeSlotsSelection';
import EventColor from '../Form/EventColor';
import EventDate from '../Form/EventDate';
import EventName from '../Form/EventName';
import EventPrograms from '../EventPrograms';
import EventDisabledOverlay from '../EventDisabledOverlay';
import { EventGrid, EventRow, FilterLabel, ErrorMessage } from './elements';

const { beforeToday } = DateRangePicker;

const DateRangeOptions = [
  {
    label: 'This Week',
    value: [
      moment().toDate(),
      moment()
        .endOf('week')
        .toDate()
    ]
  },
  {
    label: 'Next 2 Weeks',
    value: [
      moment()
        .add(1, 'weeks')
        .startOf('week')
        .toDate(),
      moment()
        .add(2, 'weeks')
        .endOf('week')
        .toDate()
    ]
  },
  {
    label: 'Next Month',
    value: [
      moment()
        .add(1, 'months')
        .startOf('month')
        .toDate(),
      moment()
        .add(1, 'months')
        .endOf('month')
        .toDate()
    ]
  }
];

const toTimeSlotFormat = requestTime => moment(requestTime).format('hh:mm A');

const setSelectedPrograms = (eventPrograms, selectedPrograms) => {
  const selectedProgramsById = zipObject(selectedPrograms, selectedPrograms);
  return map(
    each => ({
      ...each,
      isSelected: has(each.id, selectedProgramsById)
    }),
    eventPrograms
  );
};

function EventsSelection({
  onSelect,
  guestId,
  selectedEventId,
  selectedPrograms,
  defaultEvent,
  value
}) {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [outreachOn, setOutreachOn] = useState([]);
  const [showAvailableEventsOnly, setShowAvailableEventsOnly] = useState(false);
  const loading = useSelector(getIsFetching);
  const events = useSelector(getOrderedEvents);
  const meta = useSelector(getMeta);
  const allEvents = useMemo(
    () => (defaultEvent ? uniqBy('id', [defaultEvent, ...events]) : events)
  );
  const hasEvents = !isEmpty(allEvents);
  const hasMoreEvents =
    !loading && hasEvents && get('count', meta) === get('limit', meta);

  const filteredEvents = useMemo(
    () =>
      showAvailableEventsOnly
        ? filter('isGuestEligible', allEvents)
        : allEvents,
    [allEvents, showAvailableEventsOnly]
  );

  const handleChangeDateFilter = dateFilter => {
    setOutreachOn(dateFilter);
    dispatch(clearReservationAvailableEvents());
    dispatch(
      fetchReservationAvailableEvents({
        page,
        guestId,
        programId: selectedPrograms,
        outreachOn: dateFilter
      })
    );
  };

  const handleSelectTimeSlot = (outreachId, date) => timeSlot => {
    const time = moment(timeSlot, 'LT').format('HH:mm:ss');
    onSelect({ outreachId, requestTime: `${date} ${time}` });
  };

  const handleShowIneligibleEvents = () => {
    const newValue = !showAvailableEventsOnly;
    setShowAvailableEventsOnly(newValue);
    setShowAvailableEventsOnlyCookie(newValue ? '1' : '0');
  };

  const handleShowMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    dispatch(
      fetchReservationAvailableEvents({
        guestId,
        programId: selectedPrograms,
        outreachOn,
        page: nextPage
      })
    );
  };

  useEffect(() => {
    setShowAvailableEventsOnly(getShowAvailableEventsOnlyCookie() === '1');
  }, []);

  useEffect(
    () => {
      dispatch(clearReservationAvailableEvents());
      dispatch(
        fetchReservationAvailableEvents({
          programId: selectedPrograms,
          guestId,
          page: 1
        })
      );
      setPage(1);

      return () => {
        setPage(1);
        dispatch(clearReservationAvailableEvents());
      };
    },
    [selectedPrograms]
  );

  return (
    <EventGrid fluid gutter={16}>
      {(hasEvents || !isEmpty(outreachOn)) && (
        <Row style={{ marginTop: 2, marginBottom: 5 }}>
          <Col xs={24} sm={6}>
            <FilterLabel>Date Filter:</FilterLabel>
          </Col>
          <Col xs={24} sm={18}>
            <DateRangePicker
              size="sm"
              format="MM-DD-YYYY"
              ranges={DateRangeOptions}
              disabledDate={beforeToday()}
              onChange={handleChangeDateFilter}
              disabled={loading}
              style={{ marginLeft: 4, width: 250, display: 'inline-block' }}
              placeholder="Choose Event Date"
            />
            {!isEmpty(allEvents) && (
              <Checkbox
                onChange={handleShowIneligibleEvents}
                checked={showAvailableEventsOnly}
                inline
              >
                Show Available Outreaches Only
              </Checkbox>
            )}
          </Col>
        </Row>
      )}
      {map(
        ({
          id,
          date,
          name,
          color,
          schedule,
          trackingMethods,
          isGuestEligible
        }) => {
          const outreachDate = moment(date);
          const isSelected = selectedEventId === id;
          const allowedEvent = isSelected || isGuestEligible;
          return (
            <EventRow key={`event-${id}`}>
              {!allowedEvent && <EventDisabledOverlay />}
              <Col xs={24} sm={6} style={{ marginBottom: 5, marginTop: 5 }}>
                <h5>{outreachDate.format('dddd')}</h5>
                <EventDate>{outreachDate.format('MMM D, YYYY')}</EventDate>
                <EventName>
                  <EventColor color={OutreachColors[color]} /> {name}
                </EventName>
                <EventPrograms
                  isSelected={isSelected}
                  programs={setSelectedPrograms(
                    trackingMethods,
                    selectedPrograms
                  )}
                />
              </Col>
              <Col xs={24} sm={18} style={{ marginTop: 5 }}>
                <TimeSlotsSelection
                  timeSlots={schedule}
                  onSelect={handleSelectTimeSlot(id, date)}
                  selected={isSelected ? toTimeSlotFormat(value) : ''}
                />
              </Col>
            </EventRow>
          );
        },
        filteredEvents
      )}
      <Row>
        {loading ? (
          <Col xs={24} style={{ marginTop: 6 }}>
            <Loader />
          </Col>
        ) : (
          isEmpty(filteredEvents) && (
            <Col xs={24} style={{ margin: 3, textAlign: 'center' }}>
              <ErrorMessage>No available events.</ErrorMessage>
            </Col>
          )
        )}
      </Row>
      <Row>
        <Col xs={24} style={{ textAlign: 'center', margin: '10px 0' }}>
          {hasMoreEvents && (
            <Button size="sm" appearance="primary" onClick={handleShowMore}>
              Show more
            </Button>
          )}
        </Col>
      </Row>
    </EventGrid>
  );
}

EventsSelection.propTypes = {
  value: PropTypes.string,
  guestId: PropTypes.number,
  selectedEventId: PropTypes.number,
  selectedPrograms: PropTypes.arrayOf(PropTypes.number).isRequired,
  defaultEvent: PropTypes.shape({}),
  onSelect: PropTypes.func.isRequired
};

EventsSelection.defaultProps = {
  value: '',
  guestId: null,
  selectedEventId: null,
  defaultEvent: null
};

export default EventsSelection;
